import Tooltip from '../../common/Tooltip'
import Box from '@mui/material/Box'
import { TableConfig } from '../../common/Table'
import { AdPod, AdPodStatus, ListAdPodSortableField, User } from 'common/api/v1/types'
import Circle from '@mui/icons-material/FiberManualRecord'
import Round from '@mui/icons-material/FiberManualRecordOutlined'
import HelpOutline from '@mui/icons-material/HelpOutline'
import { Theme } from '@mui/material/styles'
import { AppDispatch } from '../../../store'
import { ActionMenu } from './ActionMenu'
import Checkbox from '@mui/material/Checkbox'
import { format } from 'date-fns'
import { DATE_FORMAT_LONG, DATE_FORMAT_SHORT, formatFileSize, formatMs } from 'common/api/v1/helpers'
import Typography from '@mui/material/Typography'
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined'
import { Link } from '../../common/Link'
import { Routes } from '../../../utils/routes'
import { canEditAdPod } from './List'

type AdPodColumn = TableConfig<AdPod>[number]

const styles = {
  inlineWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& > span': {
      marginLeft: (theme: Theme) => theme.spacing(1),
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    pointerEvents: 'all',
    '& svg': {
      display: 'block',
    },
  },
  warning: {
    color: (theme: Theme) => theme.palette.warning.main,
  },
  error: {
    color: (theme: Theme) => theme.palette.error.main,
  },
  success: {
    color: (theme: Theme) => theme.palette.success.main,
  },
}

const statusText = (status: AdPodStatus) => {
  switch (status) {
    case AdPodStatus.pending:
      return 'Pending: Waiting for creatives to be uploaded'
    case AdPodStatus.healthy:
      return 'Healthy'
    case AdPodStatus.unhealthy:
      return 'Unhealthy: One or more creatives are unhealthy'
    case AdPodStatus.inactive:
      return 'Inactive: No outputs are using this ad pod'
    default:
      return 'Unknown'
  }
}

const StatusIcon = ({ status }: { status: AdPodStatus }) => {
  switch (status) {
    case AdPodStatus.pending:
      return <PendingOutlinedIcon sx={styles.success} />
    case AdPodStatus.healthy:
      return <Circle sx={styles.success} />
    case AdPodStatus.unhealthy:
      return <Circle sx={styles.warning} />
    case AdPodStatus.inactive:
      return <Round sx={styles.success} />
    default:
      return <HelpOutline sx={styles.warning} />
  }
}

const status = (): AdPodColumn => ({
  title: 'status',
  getValue: ({ status }) => (
    <Tooltip title={statusText(status)} placement="top">
      <Box sx={styles.container}>
        <div>
          <StatusIcon status={status} />
        </div>
      </Box>
    </Tooltip>
  ),
})

const name = (
  { availableFn, toFn }: { availableFn: (adPod: AdPod) => boolean; toFn: (adPod: AdPod) => string },
  override?: Partial<Pick<NonNullable<AdPodColumn>, 'props'>>,
): AdPodColumn => ({
  title: 'name',
  getValue: (adPod) => (
    <Link available={availableFn(adPod)} to={toFn(adPod)} underline="hover">
      <Typography data-test-id="ad-pod-name">{adPod.name}</Typography>
    </Link>
  ),
  sorting: {
    byParameter: ListAdPodSortableField.name,
  },
  props: {
    ...override?.props,
  },
})

const duration = (): AdPodColumn => ({
  title: 'duration',
  getValue: ({ creatives }) => {
    if (creatives.length > 0) {
      const totalDurationMs = creatives.reduce((acc, creative) => {
        if (creative.userDurationMs) {
          return acc + creative.userDurationMs
        }

        if (creative.metadata?.format?.durationMs) {
          return acc + creative.metadata.format.durationMs
        }

        return acc
      }, 0)
      return (
        <Tooltip title={`${totalDurationMs} ms`}>
          <Typography variant="body2">{formatMs(totalDurationMs)}</Typography>
        </Tooltip>
      )
    }

    return `-`
  },
})

const fileSize = (): AdPodColumn => ({
  title: 'file size',
  getValue: ({ creatives }) => {
    if (creatives.length > 0) {
      const totalSize = creatives.reduce((acc, creative) => {
        if (creative.metadata?.format?.fileSize) {
          return acc + creative.metadata.format.fileSize
        }

        return acc
      }, 0)
      return (
        <Tooltip title={`${Math.round(totalSize)} bytes`}>
          <Typography variant="body2">{formatFileSize(totalSize)}</Typography>
        </Tooltip>
      )
    }

    return `-`
  },
})

const createdAt = (): AdPodColumn => ({
  title: 'created',
  getValue: ({ createdAt }) => (
    <Tooltip title={format(new Date(createdAt), DATE_FORMAT_LONG)} placement="top">
      <Typography variant="body2">{format(new Date(createdAt), DATE_FORMAT_SHORT)}</Typography>
    </Tooltip>
  ),
  sorting: {
    byParameter: ListAdPodSortableField.createdAt,
  },
})

export interface AdPodTableConfig {
  adPods: AdPod[]
  dispatch: AppDispatch
  selected: string[]
  handleSelect: (id: string) => void
  handleSelectAll: () => void
  user: User
  routes: Routes
}

export const getConfig = ({
  adPods,
  dispatch,
  selected,
  handleSelect,
  handleSelectAll,
  routes,
  user,
}: AdPodTableConfig): AdPodColumn[] => [
  {
    title: (
      <Checkbox
        indeterminate={selected.length > 0 && selected.length < adPods.length}
        checked={selected.length === adPods.length}
        onChange={handleSelectAll}
        inputProps={{ 'aria-label': 'select all inputs' }}
        data-test-id={'select-all'}
      />
    ),
    getValue: ({ id }) => (
      <Checkbox
        checked={selected.includes(id)}
        onChange={() => {
          handleSelect(id)
        }}
        onDoubleClick={(e) => e.stopPropagation()}
      />
    ),
    props: {
      padding: 'checkbox',
    },
  },
  status(),
  name({
    toFn: (adPod) => routes.adPodsUpdate({ id: adPod.id }),
    availableFn: (adPod) => canEditAdPod(user, adPod.createdBy),
  }),
  duration(),
  fileSize(),
  createdAt(),
  {
    title: '',
    getValue: (adPod: AdPod) => <ActionMenu adPod={adPod} dispatch={dispatch} />,
  },
]

export const getSmallConfig = ({ routes, user }: Pick<AdPodTableConfig, 'routes' | 'user'>): AdPodColumn[] => [
  status(),
  name({
    toFn: (adPod) => routes.adPodsUpdate({ id: adPod.id }),
    availableFn: (adPod) => canEditAdPod(user, adPod.createdBy),
  }),
  duration(),
  fileSize(),
]
